import React from 'react';
import { observer } from 'mobx-react';

import { useParams } from 'react-router-dom';

import { Button } from 'components/Button';

import CustomTextInputField from 'components/SharedFormComponents/CustomTextInputField';
import { useStore } from 'utils/hooks/store';
import CustomModal from 'components/CustomModal';

import CustomDatePicker from 'components/SharedFormComponents/CustomDatePicker';
import CustomUserDropdownField from 'components/SharedFormComponents/CustomUserDropdownField';

import API from 'utils/api';

import Logger from 'utils/logger/Logger';

import { EventType } from 'utils/events/constants';
import { postMessage } from 'utils/events/broadcast';

import { CreateTaskData } from 'utils/api/modules/tasks/tasks.types';

import { InspectionEntity } from 'utils/api/modules/workflows/workflows.types';

import { DetailsRowContainer, StyledContainer, TitleText } from './styles';

const AddActionModal: React.FunctionComponent<{
  onClose: (created: boolean) => void;
  openModal: boolean;
  extraParams?: { [key: string]: string };
  /** For hosts that link through something other than the legacy inspection, e.g. an audit. */
  links?: CreateTaskData['links'];
}> = ({ onClose, openModal, extraParams, links }): React.ReactElement => {
  const { notification } = useStore();

  const [name, setName] = React.useState('');
  const [assigned, setAssigned] = React.useState('');
  const [dueDate, setDueDate] = React.useState('');
  const [disableCreate, setDisableCreate] = React.useState(false);

  const params = useParams<{ formId: string }>();

  const { formId } = params;

  const handleCreate = async (): Promise<void> => {
    setDisableCreate(true);
    try {
      let props: CreateTaskData = {
        name,
        assigned,
        dueDate: dueDate || undefined,
        site: extraParams?.sites || null,
        asset: extraParams?.assets || null,
        contact: extraParams?.contacts || null,
        risk: extraParams?.risks || null,
        event: extraParams?.events || null,
        document: extraParams?.documents || null,
      };
      if (links) {
        props = { ...props, links };
      } else if (formId) {
        props = {
          ...props,
          links: [
            {
              entity: InspectionEntity,
              instance: formId,
            },
          ],
        };
      }
      const { data } = await API.tasks.createTaskForForm(props)();
      handleClose(true);
      setDisableCreate(false);
      postMessage(EventType.CreatedTask, data);
      notification.enqueueSuccessSnackbar('Task created successfully');
    } catch (error) {
      setDisableCreate(false);
      Logger.error('Error creating task', error);
      notification.enqueueErrorSnackbar('Error creating task');
    }
  };

  const handleClose = (created: boolean): void => {
    onClose(created);
    setName('');
    setAssigned('');
    setDueDate('');
    setDisableCreate(false);
  };

  return (
    <CustomModal
      openModal={openModal}
      onClose={() => handleClose(false)}
      modalTitle="Create New Task"
      cancelButton={
        <Button
          id="cancel-button"
          onClick={() => handleClose(false)}
          variant="outlined"
          size="large"
          data-testid="cancelButton"
        >
          Cancel
        </Button>
      }
      submitButton={
        <Button
          id="create-action-button"
          onClick={handleCreate}
          disabled={name === '' || disableCreate}
          variant="contained"
          size="large"
          data-testid="addButton"
        >
          Create Task
        </Button>
      }
    >
      <DetailsRowContainer>
        <TitleText>Task Name</TitleText>
        <CustomTextInputField
          value={name}
          onChange={(e) => setName(e as unknown as string)}
          description="Enter task name"
        />
      </DetailsRowContainer>

      <StyledContainer>
        <DetailsRowContainer>
          <TitleText>Assignee</TitleText>
          <CustomUserDropdownField
            value={assigned}
            onChange={(value) => {
              setAssigned(value ? value.uuid : '');
            }}
            description="Assigned To"
          />
        </DetailsRowContainer>

        <DetailsRowContainer>
          <TitleText>Due Date</TitleText>
          <CustomDatePicker
            tzFree
            value={dueDate || undefined}
            onChange={(val) => setDueDate((val as string | null) ?? '')}
            description=""
            size="medium"
          />
        </DetailsRowContainer>
      </StyledContainer>
    </CustomModal>
  );
};

export default observer(AddActionModal);
